import { AppBar, Badge, Box, Button, Chip, IconButton, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Toolbar, Typography, useMediaQuery } from '@mui/material'
import { AutoAwesomeOutlined, EditNote, FactCheck, FactCheckOutlined, HelpOutline, Logout, MenuOutlined, Settings, SettingsOutlined, TaskAltOutlined } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb } from './breadcrumb';
import { AvatarDisplay } from '../../components/presentation-components/display-elements-components/avatar-display'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { UserDetailsContext } from '../../context/user-details/user-details';
import UserAuthService from '../../shared/services/user-auth/user-auth.service';
import VehicleService from '../../shared/services/vehicle/vehicle.service';
import LocationService from '../../shared/services/location/location.service';
import GroupService from '../../shared/services/group/group.service';
import { TooltipHover } from '../../components/presentation-components/miscellaneous-components/tooltip-hover';
import gaiaTheme from '../theme';
import { ReportPeriodContext } from '../../context/report-periods/report-periods';
import { ReportPeriodSelectComponent } from '../../components/presentation-components/miscellaneous-components/report-period-select';
import { MobileNavbar } from './mobile-navbar';
import { useIsReportPeriodLocked } from '../../hooks/use-is-report-period-locked';
import { CustomThemeContext } from '../../context/theme/custom-theme-context';
import GlobalSearch from '../../components/presentation-components/display-elements-components/global-search';
import { CopilotPanel } from '../../components/copilot/CopilotPanel';
import AiCopilotService from '../../shared/services/ai-copilot/ai-copilot.service';
import { DraftReportSectionType, IChatTurn } from '../../shared/services/ai-copilot/ai-copilot.model';
import { useOutstandingReportActions } from '../../hooks/use-outstanding-report-actions';

export const TopNavbar: FunctionComponent<{
  showActionsNav: boolean,
  showActionsHandler: () => void,
  onCopilotOpenChange?: (open: boolean) => void,
  /** Width in px of the left rail chrome (0 when the rail is hidden), so the AppBar
   *  tracks the rail whether it is collapsed (56px) or expanded (240px). */
  leftNavWidth: number,
}> = ({
  showActionsNav,
  showActionsHandler,
  onCopilotOpenChange,
  leftNavWidth, }) => {
    const location = useLocation();
    const navigate = useNavigate();
    //Services
    const userAuthService = new UserAuthService();
    const aiCopilotService = new AiCopilotService();
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails;
    const reportPeriodContext = useContext(ReportPeriodContext)
    const customThemeContext = useContext(CustomThemeContext);
    const vehicleService = VehicleService.getInstance();
    const locationService = LocationService.getInstance();
    const groupService = GroupService.getInstance();
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State 
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openDropDown = Boolean(anchorEl);
    const [openMobileNavBar, setOpenMobileNavBar] = useState(false);
    const [copilotOpen, setCopilotOpen] = useState(false);
    //Variables
    const isActiveParterSettings: boolean = location.pathname.startsWith('/partner-settings')
    const isActiveSettings: boolean = location.pathname.startsWith('/settings');
    const isActiveChecklist: boolean = location.pathname.startsWith('/readiness');
    const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerName : 'Gaia';
    const canUseCopilot = !!userDetails?.organisation?.aiCopilotEnabled && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient);
    const selectedReportPeriodId = reportPeriodContext.lastSelectedReportId;
    const emissionDetailId = location.pathname.match(/^\/emissions\/overview\/([0-9a-f-]{36})$/i)?.[1];
    const ghgReportPeriodId = location.pathname.match(/^\/reporting\/greenhouse-gas-emissions-report\/([0-9a-f-]{36})$/i)?.[1];

    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
    const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    // Badge on the Actions icon: the period's outstanding count, fetched only where
    // the icon actually renders (count is what matters, so one row is enough).
    const canSeeActionsIcon = !isMobileMode
      && !(location.pathname === '/email-verification' || location.pathname === '/complete-sign-up')
      && !!userDetails?.organisation.subscriptionActive
      && (userDetails?.roles.includes('admin') === true || userDetails?.roles.includes('analyst') === true)
      && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient);
    const { count: outstandingActionsCount } = useOutstandingReportActions(canSeeActionsIcon, 1);
    // AppBar spans to the right edge of the viewport (minus the left icon rail only).
    // Actions and G.AI panes are fixed on the right below the toolbar — shrinking the AppBar caused a visible gap.

    //Use Effects
    useEffect(() => {
      setIsLoggedIn(!!userDetailsContext.userDetails)
    }, [location.pathname, userDetailsContext.userDetails])

    useEffect(() => {
      onCopilotOpenChange?.(copilotOpen);
    }, [copilotOpen, onCopilotOpenChange])

    //Component Event Handlers
    const logoutHandler = () => {
      customThemeContext.resetToBaseTheme();
      userAuthService.logoutUser()
        .then(() => {
          userDetailsContext.clearUserDetails();
          reportPeriodContext.clearData();
          vehicleService.clearData();
          groupService.clearData();
          locationService.clearData();
          navigate('/login')
        })
    }

    const capitaliseName = (): string => {
      if (userDetails) {
        const capitaiseFirstName = userDetails.firstName.charAt(0).toUpperCase() + userDetails.firstName.slice(1).toLowerCase()
        const capitaliseSurname = userDetails.surname.charAt(0).toUpperCase() + userDetails.surname.slice(1).toLowerCase()
        return `${capitaiseFirstName} ${capitaliseSurname}`;
      }
      return ''
    };

    const toggleDrawer = () => {
      setOpenMobileNavBar((prevOpenMobileNavBar) => !prevOpenMobileNavBar);
    }

    const handleActionsNavClick = () => {
      if (!showActionsNav) {
        setCopilotOpen(false);
      }
      showActionsHandler();
    };

    const handleCopilotToggle = () => {
      const openingCopilot = !copilotOpen;
      if (openingCopilot && showActionsNav) {
        showActionsHandler();
      }
      setCopilotOpen(openingCopilot);
    };

    const draftReportSection = (sectionType: DraftReportSectionType) => {
      const reportPeriodId = ghgReportPeriodId || selectedReportPeriodId;
      if (!reportPeriodId) return Promise.reject(new Error('Please select a report period first.'));
      return aiCopilotService.draftReportSection({ reportPeriodId, sectionType });
    }

    const askCopilotReportQuestion = (userQuestion: string, history?: IChatTurn[]) => {
      // The report page being viewed wins over the global period selector, matching
      // draftReportSection above: a question asked while reading the 2026 report must be
      // answered about 2026 even when the nav selector sits on another year.
      const rid = ghgReportPeriodId || selectedReportPeriodId || '';
      if (!rid) return Promise.reject(new Error('Please select a report period first.'));
      return aiCopilotService.askReportQuestion({
        reportPeriodId: rid,
        userQuestion,
        history,
      });
    }

    const copilotRequest = () => {
      if (emissionDetailId) {
        return aiCopilotService.explainEmission({ emissionId: emissionDetailId });
      }
      if (ghgReportPeriodId) {
        return aiCopilotService.draftReportSection({ reportPeriodId: ghgReportPeriodId, sectionType: 'performance_summary' });
      }
      if (selectedReportPeriodId) {
        return askCopilotReportQuestion('Summarise the selected reporting period using the same totals shown on the dashboard.');
      }
      return Promise.reject(new Error('Please select a report period first.'));
    }

    const copilotFollowUpRequest = (userQuestion: string, history?: IChatTurn[]) => {
      if (emissionDetailId) {
        return aiCopilotService.explainEmission({ emissionId: emissionDetailId, userQuestion, history });
      }
      if (!selectedReportPeriodId && !ghgReportPeriodId) return Promise.reject(new Error('Please select a report period first.'));
      return askCopilotReportQuestion(userQuestion, history);
    }

    const copilotSuggestedActions = [
      ...(emissionDetailId ? [
        { label: 'Why is this high?', question: 'Why is this high?' },
        { label: 'How can I improve this data?', question: 'How can I improve this data?' },
      ] : []),
      ...(selectedReportPeriodId || ghgReportPeriodId ? [
        {
          label: 'Summarise reporting period',
          request: () => askCopilotReportQuestion('Summarise the selected reporting period using the same totals shown on the dashboard.'),
        },
        {
          label: 'Explain total emissions',
          request: () => askCopilotReportQuestion('What are the total emissions and how do the scope totals add up?'),
        },
        {
          label: 'Compare to previous year',
          question: 'How does this reporting period compare to the previous calendar year?',
        },
        { label: 'Suggest data quality improvements', request: () => aiCopilotService.dataQualitySuggestions({ reportPeriodId: ghgReportPeriodId || selectedReportPeriodId || '' }) },
        { label: 'Draft methodology wording', request: () => draftReportSection('methodology') },
      ] : []),
    ];

    return (
      <>
        <AppBar
          position='fixed'
          variant='outlined'
          elevation={0}
          sx={(theme) => ({
            width: isMobileMode ? '100%' : `calc(100% - ${leftNavWidth}px)`,
            ml: `${leftNavWidth}px`,
            backgroundColor: 'common.white',
            borderLeft: 'none',
            borderRight: 'none',
            borderTop: 'none',
            zIndex: theme.zIndex.drawer + 2,
            // No width transition: the rail is a structural term that snaps (collapse /
            // expand toggle), and animating width while ml snaps opens a gap on the right.
          })}
        >
          <Toolbar sx={{ minHeight: '64px' }}>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                {!isMobileMode &&
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <Breadcrumb />
                  </Box>}
                {isMobileMode && isLoggedIn &&
                  <IconButton onClick={toggleDrawer}>
                    <MenuOutlined sx={{ color: 'text.secondary', fontSize: '2rem' }} />
                  </IconButton>
                }
                <Box display='flex' sx={{ alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                  {!isLoggedIn &&
                    <Link to='/login'>
                      <Button>
                        Sign in
                      </Button>
                    </Link>
                  }
                  {isLoggedIn &&
                    <>
                      {userDetails?.organisation.subscriptionActive && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) && (
                        <GlobalSearch />
                      )}
                      {userDetails && userDetails?.organisation.freeTrialDaysRemaining && userDetails?.organisation.freeTrialDaysRemaining > 0 && !isMobileModeMd ? (
                        <TooltipHover text={`Contact the ${companyName} sales team to upgrade to the paid version`} placement={'bottom'}
                          backgroundColor={'common.white'} email={(!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerEmailAddress || 'sales@gaiacompany.io' : 'sales@gaiacompany.io'}>
                          <Chip
                            label={userDetails.organisation.freeTrialDaysRemaining === 1 ? `${userDetails?.organisation.freeTrialDaysRemaining} day left of your free trial` : `${userDetails?.organisation.freeTrialDaysRemaining} days left of your free trial`}
                            sx={{ fontWeight: 600, fontSize: '1rem', color: 'primary.dark', backgroundColor: 'secondary.light', mr: 1.5 }} />
                        </TooltipHover>
                      ) : null
                      }
                      {userDetails && userDetails?.organisation.freeTrialDaysRemaining && userDetails?.organisation.freeTrialDaysRemaining > 0 && !isMobileModeMd && !(!userDetails?.isPartner && userDetails?.partnerOrganisation) && !(userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? (
                        <Button
                          sx={{ mr: 1, minWidth: '120px' }}
                          component={Link}
                          to='https://calendly.com/gaia-technologies/gaia-carbon-accounting-intro-meeting' target='_blank'>
                          <Typography>Book Demo</Typography>
                        </Button>
                      ) : null}
                      {userDetails?.isPartner && !userDetails?.isLoggedInAsClient &&
                        <Box>
                          <IconButton component={Link} to='/partner-settings/overview' disableRipple size='medium' sx={{ p: 0, mr: 1, '&:hover': !isActiveParterSettings ? { backgroundColor: 'action.hover', borderRadius: '10px' } : undefined }}>
                            {isActiveParterSettings ? <Settings sx={{ color: 'primary.main', p: 0.5, borderRadius: '10px', backgroundColor: 'secondary.light' }} /> : <SettingsOutlined sx={{ color: 'text.secondary', p: 0.5 }} />}
                          </IconButton>
                        </Box>
                      }
                      {!isMobileMode && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) &&
                        <Box sx={{ mr: 1.5 }}>
                          <ReportPeriodSelectComponent size='small' />
                        </Box>}
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        {(!(location.pathname === '/email-verification' || location.pathname === '/complete-sign-up') && userDetails?.organisation.subscriptionActive) && (userDetails.roles.includes('admin') || userDetails.roles.includes('analyst')) && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) &&
                          <TooltipHover text={userDetails.organisation.name === null ? 'Finish setting up your company to unlock this' : 'Report Readiness'} placement={'bottom'} backgroundColor={'common.white'}>
                            {/* The span keeps the tooltip alive while the button is disabled */}
                            <span>
                              <IconButton component={Link} to='/readiness' aria-label='Report Readiness' disableRipple size='medium' disabled={userDetails.organisation.name === null} sx={{ p: 0, mr: 1, '&:hover': !isActiveChecklist ? { backgroundColor: 'action.hover', borderRadius: '10px' } : undefined }}>
                                {isActiveChecklist ? <FactCheck sx={{ color: 'primary.main', p: 0.5, borderRadius: '10px', backgroundColor: 'secondary.light' }} /> : <FactCheckOutlined sx={{ color: 'text.secondary', p: 0.5 }} />}
                              </IconButton>
                            </span>
                          </TooltipHover>
                        }
                        {/* Stays clickable on a locked period: the pane itself explains the lock
                            instead of the button silently doing nothing. */}
                        {(!(location.pathname === '/email-verification' || location.pathname === '/complete-sign-up') && userDetails?.organisation.subscriptionActive) && (userDetails.roles.includes('admin') || userDetails.roles.includes('analyst')) && !isMobileMode && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) &&
                          <TooltipHover text={userDetails.organisation.name === null ? 'Finish setting up your company to unlock this' : 'Tasks'} placement={'bottom'} backgroundColor={'common.white'}>
                            {/* The span keeps the tooltip alive while the button is disabled */}
                            <span>
                              <IconButton aria-label='Tasks' disableRipple size='medium' disabled={userDetails.organisation.name === null} onClick={handleActionsNavClick} sx={{ p: 0, mr: 1, '&:hover': !showActionsNav ? { backgroundColor: 'action.hover', borderRadius: '10px' } : undefined }}>
                                {/* Develop's semantics: the icon stays clickable on a locked period (the pane
                                    explains the lock). The badge still yields, since its count is for the open
                                    period only. */}
                                <Badge badgeContent={isReportPeriodLocked === true ? 0 : outstandingActionsCount ?? 0} color='error'>
                                  {showActionsNav ? <TaskAltOutlined sx={{ color: 'primary.main', p: 0.5, borderRadius: '10px', backgroundColor: 'secondary.light' }} /> : <TaskAltOutlined sx={{ color: 'text.secondary', p: 0.5 }} />}
                                </Badge>
                              </IconButton>
                            </span>
                          </TooltipHover>
                        }
                        {canUseCopilot && !isMobileMode &&
                          <TooltipHover text={'AI assistant'} placement={'bottom'} backgroundColor={'common.white'}>
                            <IconButton aria-label='AI assistant' disableRipple size='medium' onClick={handleCopilotToggle} sx={{ p: 0, mr: 1, '&:hover': { backgroundColor: 'action.hover', borderRadius: '10px' } }}>
                              <AutoAwesomeOutlined sx={{ color: copilotOpen ? 'primary.main' : 'text.secondary', p: 0.5, borderRadius: '10px', backgroundColor: copilotOpen ? 'secondary.light' : undefined }} />
                            </IconButton>
                          </TooltipHover>
                        }
                        {(!(location.pathname === '/email-verification' || location.pathname === '/complete-sign-up') && userDetails?.organisation.subscriptionActive) && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) &&
                          <TooltipHover text={'Settings'} placement={'bottom'} backgroundColor={'common.white'}>
                            <IconButton component={Link} to='/settings/personal-settings/your-profile' aria-label='Settings' disableRipple size='medium' sx={{ p: 0, mr: 1, '&:hover': !isActiveSettings ? { backgroundColor: 'action.hover', borderRadius: '10px' } : undefined }}>
                              {isActiveSettings ? <Settings sx={{ color: 'primary.main', p: 0.5, borderRadius: '10px', backgroundColor: 'secondary.light' }} /> : <SettingsOutlined sx={{ color: 'text.secondary', p: 0.5 }} />}
                            </IconButton>
                          </TooltipHover>
                        }
                      </Box>
                      <IconButton onClick={(e) => { setAnchorEl(e.currentTarget) }} sx={{ p: 0, ml: 0.5 }}>
                        <AvatarDisplay color={'#b3b3b3'} width={'20px'} height={'20px'} padding={1}
                          userDetails={userDetails} />
                      </IconButton>
                      <Menu anchorEl={anchorEl} open={openDropDown} onClose={(e) => { setAnchorEl(null) }} elevation={3}>
                        <Stack display='flex' flexDirection='row' justifyContent='space-between' alignItems='center' sx={{ mt: 2, mb: 2, ml: 3, mr: 3 }}>
                          <Box sx={{ mr: 2 }}>
                            <AvatarDisplay color={'#b3b3b3'} width={'20px'} height={'20px'} padding={1} userDetails={userDetails} />
                          </Box>
                          <Stack>
                            <Typography variant='subtitle1' sx={{ color: 'text.primary', p: 0, mb: 0 }}> {capitaliseName()}</Typography>
                            <Typography sx={{ color: 'text.secondary', fontWeight: 400 }}>{userDetails?.email}</Typography>
                          </Stack>
                        </Stack>
                        {!(userDetails?.isPartner && userDetails?.isLoggedInAsClient) && (
                          <>
                            <MenuItem onClick={(e) => { setAnchorEl(null) }}>
                              <ListItemButton component={Link} target='_blank' to={'https://help.gaiacompany.io/en/collections/12187063-gaia-carbon-accounting'} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                                <ListItemIcon> <HelpOutline /> </ListItemIcon>
                                <ListItemText> Help Center </ListItemText>
                              </ListItemButton>
                            </MenuItem>
                            <MenuItem onClick={(e) => { setAnchorEl(null) }}>
                              <ListItemButton component={Link} to={'/release-notes'} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                                <ListItemIcon> <EditNote /> </ListItemIcon>
                                <ListItemText> Release Notes </ListItemText>
                              </ListItemButton>
                            </MenuItem>
                          </>
                        )}
                        <MenuItem onClick={(e) => { setAnchorEl(null) }}>
                          <ListItemButton onClick={logoutHandler} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon> <Logout /> </ListItemIcon>
                            <ListItemText> Sign Out </ListItemText>
                          </ListItemButton>
                        </MenuItem>
                      </Menu>
                    </>
                  }
                </Box>
              </Box>
              {isMobileMode && !(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) &&
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <ReportPeriodSelectComponent size='small' />
                  </Box>
                </Box>
              }
            </Box>
          </Toolbar>
        </AppBar >
        <MobileNavbar
          toggleDrawer={toggleDrawer}
          openMobileNavBar={openMobileNavBar}
        />
        {canUseCopilot && (
          <CopilotPanel
            open={copilotOpen}
            title='G.AI'
            onClose={() => setCopilotOpen(false)}
            request={copilotRequest}
            followUpRequest={copilotFollowUpRequest}
            suggestedActions={copilotSuggestedActions}
            autoRun={false}
            emptyStateText='Ask G.AI about your emissions data.'
          />
        )}
      </>
    )
  }